import { cloneDeep, get } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import findResult, {
  assignValue,
  findWithCode,
  findWithCodes,
  hasKey,
  selectableResultWithDates,
} from '@/utils/resultsHelper';
import { pruneFieldErrors } from '@/utils/errors';
import { formatDate } from '@/utils/dateHelper';
import { states } from '@/constants/lifecycle';
import pagingWithFilteredResults, {
  presentationOptions,
  sortingFilters,
} from '@/constants/states';
import fields from '@/constants/fields';
import sharingCollection, { collectionPerms } from '../sharing/state/state';
import { collectionStates, collectionCategories } from '../constants';

export const imageStates = () => ({
  imageSaveStatus: states.NONE,
  imageSaveErrors: [],
  imageDestroyStatus: states.NONE,
  imageDestroyErrors: [],
});

export const openCollection = (code) => ({
  isNew: true,
  isEditing: true,
  isDirty: false,
  id: null,
  code,
  name: '',
  description: '',
  imageAdded: false,
  imageKey: Date.now(),
  permissions: {
    ...collectionPerms(true),
  },
  copy: {
    name: null,
    description: null,
  },
  ...collectionStates,
  ...imageStates(),
});

const allCollections = (
  orderBy = [{ column: 'name', direction: 'asc' }],
  singleSorting = true,
  display = presentationOptions.CARDS,
) => (
  {
    all: {
      ...pagingWithFilteredResults(),
      ...collectionStates,
      ...sortingFilters(orderBy, singleSorting),
      display,
      multiSelect: true,
    },
  }
);

const initialState = {
  newCollectionCode: uuidv4(),
  activeCollection: { open: false },
  deleteCollectionOpen: false,
  sharingCollection: { ...sharingCollection() },
  collections: {
    ...allCollections(),
    ribbon: {
      params: ['favourites'],
      ...pagingWithFilteredResults(5),
      ...sortingFilters(),
      ...collectionStates,
    },
    open: {
      results: [],
    },
    deletion: {
      results: [],
    },
  },
};

export const mapResult = (result) => selectableResultWithDates(result);

export const newCollectionProperties = (item) => {
  const copy = cloneDeep(item);
  return ({
    ...copy,
    code: null,
    uuid: copy.code,
    open: true,
  });
};

export const assignEditProps = (item) => {
  const firstRun = !item.copy || !item.isEditing;

  if (firstRun) {
    assignValue(item.copy, 'name', item.name);
    assignValue(item.copy, 'description', item.description);
  }

  if (!item.saveErrors) {
    assignValue(item, 'saveErrors', []);
  }

  if (!item.saveStatus) {
    assignValue(item, 'saveStatus', states.NONE);
  }

  assignValue(item, 'isEditing', true);
  return firstRun;
};

export const pruneCollectionErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'name')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }
  if (hasKey(properties, 'code')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.CODE);
  }
  if (hasKey(properties, 'description')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.DESCRIPTION);
  }

  return filteredErrors;
};

export const collectionStateToLoad = ({ collections }) => {
  const items = get(collections, 'collections', initialState.collections);
  const allFilters = items.all || initialState.collections.all;
  return {
    ...initialState,
    collections: {
      ...initialState.collections,
      ...allCollections(
        allFilters.orderBy,
        allFilters.singleSorting,
        allFilters.display,
      ),
    },
  };
};

export const collectionStateToSave = ({ collections }) => (
  {
    collections: {
      collections: {
        all: {
          orderBy: collections.collections.all.orderBy,
          singleSorting: collections.collections.all.singleSorting,
          display: collections.collections.all.display,
        },
      },
    },
  }
);

export const collectionsResult = (result) => ({
  createdOn: result.created_at ? formatDate(result.created_at) : '',
  updatedOn: result.updated_at ? formatDate(result.updated_at) : '',
  lastDatasetAddedOn: result.last_dataset_added_at ? formatDate(result.last_dataset_added_at) : '',
  totalDatasets: result.total_datasets ? result.total_datasets : 0,
  ...result,
});

export const findCollection = ({ collections }, { category, code }, attribute = 'code') => (
  findResult(collections, category, code, attribute)
);

export const findCollections = ({ collections }, { category }) => collections[category];

export const findAllCollections = ({ collections }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(collections, collectionCategories, codes);
};

export const findAllCollectionsWithCode = ({ collections }, { code }) => findWithCode(
  collections,
  collectionCategories,
  code,
);

export const updateCollectionDatasetsCount = (draft, items, count, add = true) => {
  findAllCollections(draft, { items }).forEach((category) => {
    items.forEach(({ code }) => {
      const item = findResult(draft.collections, category, code);
      if (item && hasKey(item, 'totalDatasets')) {
        if (add) {
          item.totalDatasets += count;
        } else {
          item.totalDatasets -= count;
        }
      }
    });
  });
};

export default initialState;
